import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useLogin } from './useAuth';

import logo_novapet_image from '@/assets/pets/logo novapet.png';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SecretInput } from '@/shared/ui/secret-input';
import { useAuthStore } from '@/stores/authStore';
import { useWorkstationStore } from '@/stores/workstationStore';

const login_schema = z.object({
  email: z.string().min(1).max(160),
  password: z.string().min(1).max(255),
});

type LoginFields = z.infer<typeof login_schema>;

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const access_token = useAuthStore((s) => s.access_token);
  const workstation_id = useWorkstationStore((s) => s.workstation_id);
  const { submitLogin, loading } = useLogin();
  const [submit_error, setSubmitError] = useState<string | null>(null);

  const form = useForm<LoginFields>({
    resolver: zodResolver(login_schema),
    defaultValues: { email: '', password: '' },
  });

  if (!workstation_id) {
    return <Navigate to="/setup/workstation" replace />;
  }
  if (access_token) {
    const next = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return <Navigate to={next ?? '/app'} replace />;
  }

  const onSubmitLogin = async (values: LoginFields): Promise<void> => {
    setSubmitError(null);
    try {
      await submitLogin(values.email.trim(), values.password);
      navigate('/app');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'invalid_credentials';
      setSubmitError(
        message.includes('user_locked')
          ? t('auth.user_locked')
          : t('auth.invalid_credentials'),
      );
    }
  };

  return (
    <main
      className="flex h-full items-center justify-center px-4"
      style={{
        background: 'radial-gradient(circle at 50% 20%, #eafcf6 0%, #f4faf8 45%, #ffffff 100%)',
      }}
    >
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <img src={logo_novapet_image} alt="NovaPet" className="h-24 w-auto object-contain" />
        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t('auth.sign_in')}</CardTitle>
            <CardDescription>{t('auth.login_subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
          <form onSubmit={form.handleSubmit(onSubmitLogin)} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email_label')}</Label>
              <Input
                id="email"
                type="text"
                autoComplete="username"
                autoFocus
                {...form.register('email')}
              />
              {form.formState.errors.email ? (
                <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password_label')}</Label>
              <SecretInput
                id="password"
                autoComplete="current-password"
                {...form.register('password')}
              />
              {form.formState.errors.password ? (
                <p className="text-sm text-destructive">{form.formState.errors.password.message}</p>
              ) : null}
            </div>
            {submit_error ? <p className="text-sm text-destructive">{submit_error}</p> : null}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t('common.loading') : t('auth.sign_in')}
            </Button>
          </form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
